import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'

import { clientLogger } from '@/services/telemetry'
import { toastError } from '@/stores/toast-store'

/** 4xx responses are caller errors — retrying them only wastes requests. */
function shouldRetry(failureCount: number, error: unknown): boolean {
  const status = (error as { status?: number } | undefined)?.status
  if (typeof status === 'number' && status >= 400 && status < 500) return false
  return failureCount < 2
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        // Keep unused data for five minutes so back-navigation is instant.
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: shouldRetry,
        retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 15_000),
      },
      mutations: {
        // Mutations are not idempotent; a blind retry can double-write.
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        clientLogger.error('Query failed', error, { queryKey: query.queryKey })
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        clientLogger.error('Mutation failed', error, {
          mutationKey: mutation.options.mutationKey,
        })
        // Mutations are user-initiated, so silent failure is never acceptable.
        toastError(
          'Não foi possível concluir a ação',
          error instanceof Error ? error.message : undefined,
        )
      },
    }),
  })
}
